import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, BookCheck, ChevronDown, FileText, ShieldCheck, Unlock } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, InlineAlert, Skeleton, SourceText, Textarea, cn, useSave } from "@ncp/ui";
import { TestEnvironmentLine, type DecisionRelaxation } from "../../../lib/decisionControls";

/** One rule a decision cites, as it stood when the instrument was built (condition 2.13). */
export interface DecisionCitation { key: string; cited: { ar: string; en: string }; status: string; gazetteReference?: string | null; verifiedBy?: string | null; verifiedAt?: string | null }
/** One check before signature; `testEnvironment` marks a register's gap the test-environment decision controls turn into a warning the signer acknowledges. */
export interface ReadinessIssue { code: string; en: string; ar: string; blocking: boolean; testEnvironment?: boolean }
/** Under the test-environment decision controls: what is relaxed and what the signer signs with outstanding. */
export interface ReadinessTestEnvironment { controls: string; relaxations: DecisionRelaxation[]; outstanding: Array<{ ar: string; en: string }>; headSigns: boolean }
export interface DecisionHold { reason: string; reasonAr?: string | null; by: string; at: string; state?: string | null }
interface Signatory { key: string; capacity: string; capacityAr: string; authority: string; authorityAr: string; signatoryName?: string | null; signatoryNameAr?: string | null; acting: boolean }
interface Departure { code: string; finding: { ar: string; en: string } }

/** What stands between the draft and its signature, as the server checks it (POST /decisions/readiness). */
export interface DecisionReadiness {
  signable: boolean;
  issues: ReadinessIssue[];
  kind: string;
  template?: { key: string; name: string; nameAr: string; version: number; status: string; pendingVersion?: number | null } | null;
  rulesRegisterVersion: string;
  citations: DecisionCitation[];
  delegationRegister: { version: number; status: string; pendingVersion?: number | null };
  signer?: Signatory | null;
  inForce: Signatory[];
  hold?: DecisionHold | null;
  departures: Departure[];
  testEnvironment?: ReadinessTestEnvironment | null;
}

/** The draft the readiness check and the preview read: what the signer has entered so far. */
export interface DecisionDraft { outcome: string; conditions: string[]; reasonEn: string | null; reasonAr: string | null; departureReasons: string | null; validFrom: string | null; validUntil: string | null; grievanceEffect: string | null; amendedTerms: string | null; amendedTermsAr: string | null; testEnvironmentAcknowledged?: boolean }

const statusTone = (s: string) => (s === "Approved" || s === "Verified" ? "success" : s === "EditedSinceApproval" || s === "NotInRegister" ? "danger" : "warning");

/**
 * The check before signature (conditions 2.13, 2.15; scenarios 6.28, 6.30, 6.31, 6.35), shown on the draft as it stands: what blocks the
 * signature, the template version and its approval, the provisions cited and their verification, the signatory the delegation in force names,
 * and – when the decision departs from the approved analysis – the signer's reasons.
 */
export function DecisionReadinessPanel({ readiness, loading, departureReasons, onDepartureReasons, draft, acknowledged = false, onAcknowledged }: {
  readiness?: DecisionReadiness; loading: boolean; departureReasons: string; onDepartureReasons: (v: string) => void; draft?: string;
  /** Under the test-environment decision controls: the signer's one tick acknowledging the approvals still outstanding. */
  acknowledged?: boolean; onAcknowledged?: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const [showCitations, setShowCitations] = useState(false);
  if (!readiness) return loading ? <Skeleton className="h-24" /> : null;
  // The registers' gaps the test-environment controls turn into warnings are listed apart, with the one tick that acknowledges them.
  const outstanding = readiness.issues.filter((i) => i.testEnvironment);
  const blocking = readiness.issues.filter((i) => i.blocking && !i.testEnvironment);
  const notes = readiness.issues.filter((i) => !i.blocking && !i.testEnvironment);
  const awaitingTick = outstanding.some((i) => i.blocking);
  const verified = readiness.citations.filter((c) => c.status === "Verified").length;
  const signer = readiness.signer ?? readiness.inForce[0];
  return (
    <div className="space-y-3 rounded-xl border border-sand-200 bg-white p-3 text-sm" aria-busy={loading}>
      <div className="flex items-center gap-2">
        <ShieldCheck className="size-4 text-aegold-700" />
        <p className="font-medium">{t("backoffice:decisionControls.readiness.title")}</p>
        {loading ? <span className="text-xs text-muted-foreground">{t("backoffice:decisionControls.readiness.checking")}</span> : null}
      </div>
      <p className="text-xs text-muted-foreground">{t("backoffice:decisionControls.readiness.hint")}</p>
      {readiness.testEnvironment ? <TestEnvironmentLine relaxations={readiness.testEnvironment.relaxations} /> : null}
      {blocking.length === 0 && !awaitingTick ? <InlineAlert tone="success" title={t("backoffice:decisionControls.readiness.signable")} /> : blocking.length === 0 ? null : (
        <InlineAlert tone="danger" title={t("backoffice:decisionControls.readiness.blocked")}>
          <ul className="mt-1 list-disc space-y-1 ps-4 text-xs">{blocking.map((i) => <li key={i.code}>{ar ? i.ar : i.en}</li>)}</ul>
        </InlineAlert>
      )}
      {outstanding.length > 0 ? (
        <InlineAlert tone="warning" title={t("backoffice:decisionControls.testEnvironment.outstanding")}>
          <ul className="mt-1 list-disc space-y-1 ps-4 text-xs">{outstanding.map((i) => <li key={i.code}>{ar ? i.ar : i.en}</li>)}</ul>
          <label className="mt-2 flex items-start gap-2 text-xs">
            <Checkbox checked={acknowledged} disabled={!onAcknowledged} onCheckedChange={(v) => onAcknowledged?.(v === true)} className="mt-0.5" />
            <span>{t("backoffice:decisionControls.testEnvironment.acknowledge")}</span>
          </label>
          {awaitingTick && !acknowledged ? <p className="mt-1 text-[11px] text-muted-foreground">{t("backoffice:decisionControls.testEnvironment.acknowledgeNeeded")}</p> : null}
        </InlineAlert>
      ) : null}
      {notes.length > 0 ? (
        <InlineAlert tone="info" title={t("backoffice:decisionControls.readiness.notes")}>
          <ul className="mt-1 list-disc space-y-1 ps-4 text-xs">{notes.map((i) => <li key={i.code}>{ar ? i.ar : i.en}</li>)}</ul>
        </InlineAlert>
      ) : null}
      {readiness.departures.length > 0 ? (
        <Field label={t("backoffice:decisionControls.readiness.departureReasons")} hint={t("backoffice:decisionControls.readiness.departureHint")}>
          {(id) => <Textarea id={id} rows={3} value={departureReasons} onChange={(e) => onDepartureReasons(e.target.value)} draft={draft} />}
        </Field>
      ) : null}
      <dl className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-[auto_1fr]">
        {readiness.template ? (<>
          <dt className="text-muted-foreground">{t("backoffice:decisionControls.readiness.template")}</dt>
          <dd className="flex flex-wrap items-center gap-1.5">
            {t("backoffice:decisionControls.readiness.templateLine", { name: ar ? readiness.template.nameAr : readiness.template.name, version: readiness.template.version })}
            <Badge size="sm" variant={statusTone(readiness.template.status)}>{t(`backoffice:decisionControls.readiness.status.${readiness.template.status}`, { defaultValue: readiness.template.status })}</Badge>
            {readiness.template.pendingVersion ? <span className="text-muted-foreground">{t("backoffice:decisionControls.readiness.pending", { version: readiness.template.pendingVersion })}</span> : null}
          </dd>
        </>) : null}
        <dt className="text-muted-foreground">{t("backoffice:decisionControls.readiness.register")}</dt>
        <dd>
          <button type="button" className="inline-flex items-center gap-1 text-start underline-offset-4 hover:underline" aria-expanded={showCitations} onClick={() => setShowCitations((v) => !v)}>
            {t("backoffice:decisionControls.readiness.registerLine", { version: readiness.rulesRegisterVersion, verified, count: readiness.citations.length })}
            <ChevronDown className={cn("size-3 transition-transform", showCitations && "rotate-180")} />
          </button>
        </dd>
        {signer ? (<>
          <dt className="text-muted-foreground">{t("backoffice:decisionControls.readiness.signatory")}</dt>
          <dd>{(ar ? signer.signatoryNameAr : signer.signatoryName) ? `${ar ? signer.signatoryNameAr : signer.signatoryName} · ` : ""}{t("backoffice:decisionControls.readiness.signatoryLine", { capacity: ar ? signer.capacityAr : signer.capacity, authority: ar ? signer.authorityAr : signer.authority })}</dd>
        </>) : null}
        <dt className="text-muted-foreground">{t("backoffice:decisionControls.readiness.delegations")}</dt>
        <dd className="flex flex-wrap items-center gap-1.5">
          {t("backoffice:decisionControls.readiness.delegationsLine", { version: readiness.delegationRegister.version })}
          <Badge size="sm" variant={statusTone(readiness.delegationRegister.status)}>{t(`backoffice:decisionControls.readiness.status.${readiness.delegationRegister.status}`, { defaultValue: readiness.delegationRegister.status })}</Badge>
        </dd>
      </dl>
      {showCitations ? <CitationList citations={readiness.citations} /> : null}
    </div>
  );
}

export function CitationList({ citations }: { citations: DecisionCitation[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  if (citations.length === 0) return <p className="text-xs text-muted-foreground">{t("backoffice:decisionControls.basis.none")}</p>;
  return (
    <ul className="divide-y divide-sand-100 rounded-lg border border-sand-200 text-xs" aria-label={t("backoffice:decisionControls.readiness.citations")}>
      {citations.map((c) => (
        <li key={c.key} className="flex flex-wrap items-start gap-2 px-3 py-2">
          <span className="min-w-0 flex-1" dir="auto">{locale === "ar" ? c.cited.ar : c.cited.en}</span>
          <Badge size="sm" variant={statusTone(c.status)}>{t(`backoffice:decisionControls.readiness.status.${c.status}`, { defaultValue: c.status })}</Badge>
          {c.gazetteReference ? <span className="w-full text-muted-foreground">{t("backoffice:decisionControls.basis.gazette", { reference: c.gazetteReference })}{c.verifiedBy && c.verifiedAt ? ` · ${t("backoffice:decisionControls.basis.verifiedBy", { by: c.verifiedBy, date: formatDate(c.verifiedAt, locale) })}` : ""}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/** The not-signable marking of the file's decision document (scenario 6.35): shown, set and lifted, each with reasons kept in the file's history. */
export function DecisionHoldControl({ caseId, hold, canMark, onChanged }: { caseId: string; hold?: DecisionHold | null; canMark: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const [open, setOpen] = useState<"mark" | "lift" | null>(null);
  const [reason, setReason] = useState("");
  const [reasonAr, setReasonAr] = useState("");
  const done = () => { setOpen(null); setReason(""); setReasonAr(""); onChanged(); };
  const mark = useSave({
    mutationFn: () => api.post(`/api/cases/${caseId}/decisions/not-signable`, { reason, reasonAr: reasonAr || null }),
    saved: t("backoffice:decisionControls.hold.markedToast"),
    onSuccess: done,
  });
  const lift = useSave({
    mutationFn: () => api.post(`/api/cases/${caseId}/decisions/not-signable/lift`, { reason }),
    saved: t("backoffice:decisionControls.hold.liftedToast"),
    onSuccess: done,
  });
  return (
    <>
      {hold ? (
        <InlineAlert tone="danger" title={t("backoffice:decisionControls.hold.marked")}>
          <p className="text-xs"><SourceText text={(locale === "ar" && hold.reasonAr) || hold.reason} /></p>
          <p className="mt-1 text-xs opacity-80">{t("backoffice:decisionControls.hold.markedBy", { by: hold.by, date: formatDateTime(hold.at, locale) })}</p>
          {canMark ? <Button size="sm" variant="outline" className="mt-2" onClick={() => setOpen("lift")}><Unlock /> {t("backoffice:decisionControls.hold.lift")}</Button> : null}
        </InlineAlert>
      ) : canMark ? (
        <Button size="sm" variant="ghost" onClick={() => setOpen("mark")}><Ban /> {t("backoffice:decisionControls.hold.mark")}</Button>
      ) : null}
      <Dialog open={open !== null} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{open === "lift" ? t("backoffice:decisionControls.hold.liftTitle") : t("backoffice:decisionControls.hold.markTitle")}</DialogTitle>
            {open === "mark" ? <DialogDescription>{t("backoffice:decisionControls.hold.markHint")}</DialogDescription> : null}
          </DialogHeader>
          <div className="space-y-3">
            <Field label={open === "lift" ? t("backoffice:decisionControls.hold.liftReason") : t("backoffice:decisionControls.hold.reason")} required>{(id) => <Textarea id={id} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} draft={open ? `case:${caseId}:decision:${open}:reason` : null} />}</Field>
            {open === "mark" ? <Field label={t("backoffice:decisionControls.hold.reasonAr")}>{(id) => <Textarea id={id} rows={3} dir="rtl" value={reasonAr} onChange={(e) => setReasonAr(e.target.value)} draft={open ? `case:${caseId}:decision:${open}:reasonAr` : null} />}</Field> : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(null)}>{t("actions.cancel")}</Button>
            <Button variant={open === "lift" ? "gold" : "destructive"} disabled={reason.trim().length < 10} loading={mark.isPending || lift.isPending} onClick={() => (open === "lift" ? lift.mutate() : mark.mutate())}>
              {open === "lift" ? t("backoffice:decisionControls.hold.lift") : t("backoffice:decisionControls.hold.mark")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface LegalBasis {
  templateKey?: string | null; templateVersion: number; rulesRegisterVersion: string; citations: DecisionCitation[]; recorded: boolean; issuedAt: string; delegationRegisterVersion: number;
  departures: Departure[]; departureReasons?: string | null;
}

/** The legal basis of an issued decision as at its date (condition 2.13): the template and register versions, and each provision as it stood. */
export function DecisionLegalBasis({ caseId, decisionId }: { caseId: string; decisionId: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const basis = useQuery({ queryKey: ["decision-legal-basis", caseId, decisionId], queryFn: () => api.get<LegalBasis>(`/api/cases/${caseId}/decisions/${decisionId}/legal-basis`), enabled: open });
  return (
    <div className="space-y-2">
      <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => setOpen((v) => !v)}><BookCheck /> {open ? t("backoffice:decisionControls.basis.close") : t("backoffice:decisionControls.basis.open")}</Button>
      {open ? basis.isLoading ? <Skeleton className="h-20" /> : basis.data ? (
        <div className="space-y-2 rounded-xl border border-sand-200 bg-white p-3 text-xs">
          {basis.data.templateKey ? <p>{t("backoffice:decisionControls.basis.built", { template: basis.data.templateKey, version: basis.data.templateVersion, register: basis.data.rulesRegisterVersion, delegations: basis.data.delegationRegisterVersion })}</p> : null}
          <p className="text-muted-foreground">{basis.data.recorded ? t("backoffice:decisionControls.basis.recorded") : t("backoffice:decisionControls.basis.rebuilt", { date: formatDate(basis.data.issuedAt, locale) })}</p>
          <CitationList citations={basis.data.citations} />
          {basis.data.departures.length > 0 ? (
            <div>
              <p className="font-medium">{t("backoffice:decisionControls.basis.departures")}</p>
              <ul className="mt-1 list-disc space-y-1 ps-4">{basis.data.departures.map((d) => <li key={d.code}>{locale === "ar" ? d.finding.ar : d.finding.en}</li>)}</ul>
              {basis.data.departureReasons ? <p className="mt-1 rounded-lg bg-sand-50 p-2"><SourceText text={basis.data.departureReasons} /></p> : null}
            </div>
          ) : null}
        </div>
      ) : null : null}
    </div>
  );
}

/** The Word download of a signed decision, built from the same instrument as the PDF (scenario 6.33). */
export function DecisionWordDownload({ caseId, decisionId }: { caseId: string; decisionId: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  return <Button size="sm" variant="outline" onClick={() => void api.download(`/api/cases/${caseId}/decisions/${decisionId}/instrument.docx?locale=${locale === "en" ? "en" : "ar"}`)}><FileText /> {t("backoffice:decisionControls.basis.word")}</Button>;
}

/** The readiness of the draft, re-checked as the signer types (debounced by the query key). */
export function useDecisionReadiness(caseId: string, draft: DecisionDraft, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ["decision-readiness", caseId, JSON.stringify(draft)],
    queryFn: () => api.post<DecisionReadiness>(`/api/cases/${caseId}/decisions/readiness`, draft),
    enabled: enabled && !!draft.outcome,
    placeholderData: (previous) => previous,
    staleTime: 5_000,
  });
}

export function useInvalidateReadiness(caseId: string) {
  const qc = useQueryClient();
  return () => { for (const k of [["decision-readiness", caseId], ["case-decisions", caseId], ["case", caseId], ["case-workflow", caseId]]) void qc.invalidateQueries({ queryKey: k }); };
}
